import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { useForm, useWatch, type Path, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import NotesIcon from '@mui/icons-material/Notes';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { v4 as uuid } from 'uuid';
import { useRxCollection } from 'rxdb/plugins/react';
import {
    formatFindSlipNumber,
    sampleInputSchema,
    SAMPLE_TYPE_OPTIONS,
    SAMPLING_METHOD_OPTIONS,
    type FindDocType,
    type SampleDocType
} from '@ast-data/shared';
import { useValidationMessage } from '../../i18n/validationMessages';
import { useAbility, scopedSubject } from '../auth/abilityContext';
import { useRecordTranslations } from '../translation/recordTranslations';
import { useSavedToast } from '../../components/toastContext';
import { useFamilyDocs, useProjectDocs } from '../../db/useScopedDocs';
import { useFamilyProjectIds } from '../projects/useFamilyProjectIds';
import { useCurrentPerson, usePersonUserId } from '../users/personUserId';
import { useSelfPrefill } from '../users/useSelfPrefill';
import { usePersonPicker } from '../../components/personPicker';
import { useEmploymentDay } from '../users/employment';
import RecordedAtField from '../../components/RecordedAtField';
import RecordLinkChip from '../../components/RecordLinkChip';
import {
    bindFormFields,
    labelIdsFrom,
    requiredInForm,
    useFormRecentValues
} from '../../components/formFields';
import FormRow from '../../components/FormRow';
import FormSectionBox from '../../components/FormSectionBox';
import { SectionNavMenuButton } from '../../components/formSectionNav';
import { type FormSection as FormSectionNavEntry } from '../../components/formSectionNavContext';
import FormDialogAppBar from '../../components/FormDialogAppBar';
import RecordFormDialog from '../../components/RecordFormDialog';
import { useGuardedNavigate } from '../../components/useGuardedNavigate';
import { useRecordSiblings } from '../../components/useRecordSiblings';
import { recordSortModel } from '../../components/useRecordGrid';
import {
    useForwardBackState,
    useDialogBackCrumb
} from '../../components/dialogBackTrail';
import { useUnsavedCloseGuard } from '../../components/useUnsavedCloseGuard';
import { useFocusFieldOnOpen } from '../../components/cellFocus';
import { MediaSectionBox } from './descriptionSections';
import { SAMPLE_GRID } from './recordGrids';
import { useInlineSampleFields } from './useSamplesColumns';
import { useFindingRefSupport } from './useFindingRefSupport';
import { useFamilyDescriptions } from './useFamilyDescriptions';
import FindingDescriptionPicker from './FindingDescriptionPicker';
import {
    type FindingRefs,
    emptyFindingRefs,
    hasFindingRefs,
    isOwnLocationFilled,
    NO_OWN_LOCATION
} from './findingRefSets';
import { distinct } from './diaryFormValues';
import { sampleMediaLocation } from './sampleLocation';
import { sampleLabel } from './sampleLabel';
import { usePresetRefs } from './presetRefs';
import { compareNatural } from '../../utils/compare';

type FormValues = {
    sampleType: string;
    samplingReason: string;
    samplingMethod: string;
    description: string;
    sampler: string;
    area: string;
    trench: string;
    planum: string;
    profile: string;
    recordedAt: string;
};

function emptyValues(): FormValues {
    return {
        sampleType: '',
        samplingReason: '',
        samplingMethod: '',
        description: '',
        sampler: '',
        area: '',
        trench: '',
        planum: '',
        profile: '',
        recordedAt: new Date().toISOString()
    };
}

function toValues(sample: SampleDocType): FormValues {
    return {
        sampleType: sample.sampleType,
        samplingReason: sample.samplingReason,
        samplingMethod: sample.samplingMethod,
        description: sample.description,
        sampler: sample.sampler,
        area: sample.area,
        trench: sample.trench,
        planum: sample.planum,
        profile: sample.profile,
        recordedAt: sample.recordedAt
    };
}

const FORM_SECTIONS: FormSectionNavEntry[] = [
    { id: 'identity', titleId: 'sample.section.identity' },
    { id: 'location', titleId: 'sample.section.location' },
    { id: 'sampling', titleId: 'sample.section.sampling' },
    { id: 'finds', titleId: 'sample.section.finds' },
    { id: 'media', titleId: 'media.title' }
];

interface SampleFormDialogProps {
    open: boolean;
    onClose: () => void;
    projectId: string;
    sample?: SampleDocType | null;
    /** Grid column the user double-clicked, to focus the matching input on open. */
    focusField?: string;
}

/**
 * The person the record is written up by is required in the form — see
 * requiredInForm.
 */
const SAMPLE_FORM_SCHEMA = requiredInForm(sampleInputSchema, ['sampler']);

export default function SampleFormDialog({
    open,
    onClose,
    projectId,
    sample,
    focusField
}: SampleFormDialogProps) {
    const intl = useIntl();
    const collection = useRxCollection<SampleDocType>('samples');
    const validationMessage = useValidationMessage();
    const notifySaved = useSavedToast();
    const isEdit = !!sample;
    const inField = useLocation().pathname.startsWith('/field');

    const ability = useAbility();
    const canEditMedia = ability.can(
        'update',
        scopedSubject('Sample', { projectId })
    );
    const readOnly = !ability.can(
        isEdit ? 'update' : 'create',
        scopedSubject('Sample', { projectId })
    );
    // Converting writes a Fund — and the field tool has no Fund editor. Which
    // Funde a Probe became is a right of its own (`linkToFind`).
    const canConvert =
        !inField &&
        ability.can('create', scopedSubject('Find', { projectId })) &&
        ability.can('linkToFind', scopedSubject('Sample', { projectId }));

    // Suggestions and F2 read only this project's samples, like the Funde.
    const { results: samples } = useProjectDocs<SampleDocType>(
        'samples',
        projectId
    );
    const familyIds = useFamilyProjectIds(projectId);
    const { results: familyFinds } = useFamilyDocs<FindDocType>(
        'finds',
        familyIds
    );
    const { findings, planums, profiles } = useFamilyDescriptions(projectId);

    // The description lives outside react-hook-form, like the Fund's — seeded
    // from the doc (or, new from a description's Verweise, from the URL),
    // written back on save.
    const presetRefs = usePresetRefs(!sample);
    const linkedRefs = useMemo<FindingRefs>(
        () => ({
            planumIds: sample?.planumIds ?? presetRefs?.planumIds ?? [],
            profileIds: sample?.profileIds ?? presetRefs?.profileIds ?? [],
            findingIds: []
        }),
        [sample, presetRefs]
    );
    const [refs, setRefs] = useState<FindingRefs>(emptyFindingRefs());

    // A name nobody touched keeps its account even where the rule can no
    // longer place it (see usePersonUserId).
    const keptLinks = useMemo(
        () => (sample ? [{ name: sample.sampler, id: sample.samplerId }] : []),
        [sample]
    );
    const { resolve } = usePersonUserId(keptLinks);
    const currentUserName = useCurrentPerson()?.name ?? '';

    const {
        register,
        handleSubmit,
        reset,
        getValues,
        setFocus,
        setValue,
        control,
        formState: { errors, isSubmitting, isDirty }
    } = useForm<FormValues>({
        resolver: zodResolver(
            SAMPLE_FORM_SCHEMA
        ) as unknown as Resolver<FormValues>,
        defaultValues: emptyValues()
    });

    // Only who was employed on the Aufnahmedatum (see users/employment).
    const employmentDay = useEmploymentDay(control, 'recordedAt');
    const personPicker = usePersonPicker(employmentDay);

    // F2 offers a field its last values — from the SAME list the grid's cells
    // edit by.
    const recent = useFormRecentValues({
        personPicker,
        fields: useInlineSampleFields(samples),
        setValue: (name, value) =>
            setValue(name as Path<FormValues>, value, {
                shouldDirty: true
            })
    });

    const { text, suggest, person, richText } = bindFormFields<FormValues>({
        intl,
        readOnly,
        register,
        control,
        errors,
        validationMessage,
        labelId: labelIdsFrom('sample.field'),
        ...recent.binding
    });

    // The account names can arrive after the dialog opened (see useSelfPrefill).
    useSelfPrefill(open && !sample, currentUserName, (self) => {
        if (getValues('sampler').trim() === '') {
            setValue('sampler', self);
        }
    });

    // An effect EVENT, so `linkedRefs` is read at its current value without the
    // reset re-running (and wiping what was typed) every time RxDB re-emits.
    const resetForOpening = useEffectEvent(() => {
        const values = sample ? toValues(sample) : emptyValues();
        reset({
            ...values,
            // Default the Entnehmer to whoever is recording, like every record
            // dialog — changed by hand where somebody else took the Probe.
            sampler: values.sampler || currentUserName
        });
        setRefs(linkedRefs);
    });
    useEffect(() => {
        if (open) {
            // react-hook-form's `reset` publishes to its own subscribers, so it
            // cannot be moved into the render pass the way plain state can.
            // eslint-disable-next-line react-hooks/set-state-in-effect
            resetForOpening();
        }
    }, [open, sample]);

    useFocusFieldOnOpen(setFocus, open, focusField);

    const refsDirty = useMemo(() => {
        const key = (candidate: FindingRefs) =>
            [
                [...candidate.planumIds].sort().join(','),
                [...candidate.profileIds].sort().join(',')
            ].join('|');
        return key(refs) !== key(linkedRefs);
    }, [refs, linkedRefs]);

    const translations = useRecordTranslations({
        ownerType: 'sample',
        ownerId: sample?.id,
        projectId,
        readOnly
    });
    const closeGuard = useUnsavedCloseGuard({
        open,
        isDirty: isDirty || refsDirty || translations.isDirty,
        onClose,
        onSave: () => handleSubmit(onSubmit)()
    });
    const selfLabel = sample
        ? sampleLabel(intl, sample)
        : intl.formatMessage({ id: 'sample.form.newTitle' });
    const guardedNav = useGuardedNavigate({
        isDirty: isDirty || refsDirty || translations.isDirty,
        submit: () => handleSubmit(onSubmit)(),
        close: () => closeGuard.close(),
        forwardState: useForwardBackState(selfLabel)
    });
    const backCrumb = useDialogBackCrumb(closeGuard.requestBack);
    const siblings = useRecordSiblings<SampleDocType>({
        collection: SAMPLE_GRID.entity.collection,
        projectId,
        currentId: sample?.id ?? '',
        sortModel: recordSortModel(SAMPLE_GRID),
        label: (row) => sampleLabel(intl, row),
        requestNavigate: guardedNav.requestNavigate
    });

    // Defined AFTER `guardedNav`: a ringed "Bef. 7000" is a link out of an open
    // editor, so it has to ask before leaving.
    const findingRefs = useFindingRefSupport(
        projectId,
        guardedNav.requestNavigate
    );

    async function onSubmit(data: FormValues) {
        if (!collection || readOnly) {
            return;
        }

        const now = new Date().toISOString();
        // The resolver hands back the PARSED input, `findIds: []` default
        // included — written back, it would cut the Probe off from its Funde.
        // They are written only by the conversion (FindFormDialog).
        const { findIds: _findIds, ...fields } = data as FormValues & {
            findIds?: string[];
        };
        const content = {
            ...fields,
            // Either a Befund or an own Lage: a linked record drops the Lage
            // its hidden inputs may still hold (see NO_OWN_LOCATION).
            ...(hasFindingRefs(refs) ? NO_OWN_LOCATION : {}),
            // '' for free text — so the crew dedups by user, not just by name.
            samplerId: resolve(data.sampler),
            planumIds: refs.planumIds,
            profileIds: refs.profileIds
        };
        const savedId = isEdit ? sample!.id : uuid();
        if (isEdit) {
            const doc = await collection.findOne(savedId).exec();
            if (doc) {
                await doc.incrementalPatch({ ...content, updatedAt: now });
            }
        } else {
            await collection.insert({
                id: savedId,
                projectId,
                ...content,
                findIds: [],
                createdBy: '',
                updatedBy: '',
                updatedAt: now,
                createdAt: now
            });
        }
        await translations.commit(savedId);
        notifySaved();
        // A pending "save and go" target if there is one, else close.
        guardedNav.finishSubmit();
    }

    // The Funde this Probe became, by Fundzettel. A Fund since deleted simply
    // drops out of the list.
    const madeFinds = useMemo(() => {
        const ids = new Set(sample?.findIds ?? []);
        return familyFinds
            .filter((find) => ids.has(find.id))
            .sort((firstFind, secondFind) =>
                compareNatural(
                    formatFindSlipNumber(firstFind),
                    formatFindSlipNumber(secondFind)
                )
            );
    }, [sample, familyFinds]);

    // What a downloaded picture is named after — read live, so a date just
    // changed is already in the file name.
    const [watchedRecordedAt, area, trench, planum, profile] = useWatch({
        control,
        name: ['recordedAt', 'area', 'trench', 'planum', 'profile']
    });
    const mediaLocation = useMemo(
        () =>
            sampleMediaLocation(
                watchedRecordedAt,
                refs,
                findings,
                planums,
                profiles,
                { area, planum, profile }
            ),
        [
            watchedRecordedAt,
            refs,
            findings,
            planums,
            profiles,
            area,
            planum,
            profile
        ]
    );
    // Either a description or an own Lage, as on the Fund: once the Lage is
    // typed, the picker goes; once a description is linked, the Lage does. A
    // Probe that already carries both keeps the picker, so the link can be
    // removed.
    const hasOwnLocation = isOwnLocationFilled([area, trench, planum, profile]);
    const showDescriptionPicker = hasFindingRefs(refs) || !hasOwnLocation;
    const suggestions = useMemo(
        () => ({
            area: distinct(samples.map((row) => row.area)),
            trench: distinct(samples.map((row) => row.trench)),
            planum: distinct(samples.map((row) => row.planum)),
            profile: distinct(samples.map((row) => row.profile))
        }),
        [samples]
    );

    return (
        <RecordFormDialog
            open={open}
            sections={FORM_SECTIONS}
            onSubmit={handleSubmit(onSubmit)}
            closeGuard={closeGuard}
            navGuard={guardedNav}
            isSubmitting={isSubmitting}
            readOnly={readOnly}
            translations={translations}
            appBar={
                <FormDialogAppBar
                    projectId={projectId}
                    onClose={closeGuard.requestClose}
                    startSlot={<SectionNavMenuButton />}
                    backCrumb={backCrumb}
                    siblings={siblings}
                    title={
                        isEdit && sample ? (
                            sampleLabel(intl, sample)
                        ) : (
                            <FormattedMessage
                                id={
                                    isEdit
                                        ? 'sample.form.editTitle'
                                        : 'sample.form.newTitle'
                                }
                            />
                        )
                    }
                />
            }
        >
            <FormSectionBox titlePrefix="sample.section" id="identity">
                <FormRow>
                    {suggest('sampleType', SAMPLE_TYPE_OPTIONS)}
                    <RecordedAtField
                        control={control}
                        name="recordedAt"
                        labelId="sample.field.recordedAt"
                        readOnly={readOnly}
                    />
                </FormRow>
                <FormRow>{person('sampler', personPicker)}</FormRow>
            </FormSectionBox>

            <FormSectionBox titlePrefix="sample.section" id="location">
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    <FormattedMessage id="sample.location.hint" />
                </Typography>
                {showDescriptionPicker && (
                    <FindingDescriptionPicker
                        projectId={projectId}
                        value={refs}
                        onChange={setRefs}
                        descriptionsOnly
                        single
                        label={intl.formatMessage({
                            id: 'sample.field.descriptions'
                        })}
                        onOpenRef={guardedNav.requestNavigate}
                    />
                )}
                {/* A linked description supplies the Lage. */}
                {!hasFindingRefs(refs) && (
                    <>
                        <FormRow>
                            {suggest('area', suggestions.area)}
                            {suggest('trench', suggestions.trench)}
                        </FormRow>
                        <FormRow>
                            {suggest('planum', suggestions.planum)}
                            {suggest('profile', suggestions.profile)}
                        </FormRow>
                    </>
                )}
            </FormSectionBox>

            <FormSectionBox titlePrefix="sample.section" id="sampling">
                <FormRow>
                    {suggest('samplingMethod', SAMPLING_METHOD_OPTIONS)}
                    {text('samplingReason')}
                </FormRow>
                {richText('description', {
                    icon: <NotesIcon />,
                    findingRefs
                })}
            </FormSectionBox>

            <FormSectionBox titlePrefix="sample.section" id="finds">
                {madeFinds.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {madeFinds.map((find) => (
                            <RecordLinkChip
                                key={find.id}
                                icon={<EmojiEventsIcon />}
                                label={intl.formatMessage(
                                    { id: 'find.crumb' },
                                    {
                                        number:
                                            formatFindSlipNumber(find) || '?'
                                    }
                                )}
                                to={
                                    inField
                                        ? undefined
                                        : `/office/projects/${find.projectId}/finds/${find.id}/edit`
                                }
                                onOpen={guardedNav.requestNavigate}
                            />
                        ))}
                    </Box>
                ) : (
                    <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary' }}
                    >
                        <FormattedMessage id="sample.finds.none" />
                    </Typography>
                )}
                {canConvert && (
                    <Box>
                        <Button
                            variant="outlined"
                            startIcon={<EmojiEventsIcon />}
                            // Converting reads the STORED sample, so a new one
                            // has to be saved first.
                            disabled={!isEdit}
                            onClick={() =>
                                guardedNav.requestNavigate(
                                    `/office/projects/${projectId}/finds/new?seedFrom=${sample!.id}`
                                )
                            }
                        >
                            <FormattedMessage id="sample.convert" />
                        </Button>
                        {!isEdit && (
                            <FormHelperText>
                                <FormattedMessage id="sample.convert.saveFirst" />
                            </FormHelperText>
                        )}
                    </Box>
                )}
            </FormSectionBox>

            <MediaSectionBox
                projectId={projectId}
                ownerType="sample"
                ownerId={isEdit && sample ? sample.id : null}
                canEdit={canEditMedia}
                location={mediaLocation}
                onNavigate={guardedNav.requestNavigate}
            />
            {recent.menu}
        </RecordFormDialog>
    );
}
